import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { ApiError } from "@carepass/api-client";
import { useAdminApplication } from "../../app/providers";
import { PasswordChangeView } from "./components/PasswordChangeView";
import type { LoginFeedback } from "./components/LoginView";
import { useAdminPasswordChangeCommand } from "./queries";
import type { Schemas } from "../workspace/api.generated";

type PasswordChangeFeedback = LoginFeedback & { readonly recovery?: "login" };

const PASSWORD_CHANGE_FEEDBACK: Readonly<Record<string, LoginFeedback>> = {
  ADMIN_REAUTHENTICATION_REQUIRED: {
    title: "현재 비밀번호를 확인해 주세요",
    message: "현재 비밀번호가 올바르지 않습니다.",
  },
  ADMIN_PASSWORD_POLICY_VIOLATION: {
    title: "새 비밀번호 정책을 확인해 주세요",
    message: "8~30자이며 영문 대문자, 소문자, 숫자, 특수문자를 모두 포함해 주세요.",
  },
  ADMIN_PASSWORD_REUSE: {
    title: "다른 비밀번호를 사용해 주세요",
    message: "현재 비밀번호와 다른 새 비밀번호를 입력해 주세요.",
  },
};

export function passwordChangeFeedback(error: unknown): PasswordChangeFeedback {
  if (error instanceof ApiError) {
    const known = PASSWORD_CHANGE_FEEDBACK[error.code];
    if (known) return known;
    if (error.kind === "network") {
      return {
        title: "변경 결과를 확인하지 못했습니다",
        message: "자동으로 다시 전송하지 않습니다. 다시 로그인해 비밀번호 상태를 확인해 주세요.",
        recovery: "login",
      };
    }
  }
  return {
    title: "비밀번호를 변경하지 못했습니다",
    message: "입력 정보를 확인한 뒤 다시 시도해 주세요.",
  };
}

export function PasswordChangePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sessionLifecycle } = useAdminApplication();
  const changePassword = useAdminPasswordChangeCommand();
  const [error, setError] = useState<PasswordChangeFeedback | null>(null);

  const submit = async (
    input: Schemas["ChangeAdminPasswordRequest"],
  ): Promise<"totp-enrollment" | "operations" | false> => {
    setError(null);
    try {
      const snapshot = await changePassword(input);
      return snapshot.phase === "totp-enrollment" ? "totp-enrollment" : "operations";
    } catch (cause) {
      if (cause instanceof ApiError && cause.kind === "aborted") return false;
      setError(passwordChangeFeedback(cause));
      return false;
    }
  };

  const complete = (nextPhase: "totp-enrollment" | "operations") => {
    const nextPath = nextPhase === "totp-enrollment" ? "/setup/totp" : "/login";
    void navigate(`${nextPath}${location.search}`, { replace: true });
  };

  const returnToLogin = () => {
    sessionLifecycle.end("password-change-uncertain");
    void navigate(`/login${location.search}`, { replace: true });
  };

  return (
    <PasswordChangeView
      error={error}
      onClearError={() => setError(null)}
      onComplete={complete}
      onReturnToLogin={returnToLogin}
      onSubmit={submit}
    />
  );
}
